<script setup lang="ts">
import { computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useVuelidate } from '@vuelidate/core'
import { required, requiredIf, sameAs } from '@vuelidate/validators'
import { useUmsokn } from '@/composables/Applications/UseUmsokn'

const { t } = useI18n()
const { umsokn } = useUmsokn()

const props = defineProps<{
  disable?: boolean
}>()

// Initialize motframlag fields on umsokn if not already set
if (umsokn.value) {
  // Migrate legacy motframlagHasMatch=true to percentOfPay=3
  if (umsokn.value.motframlagHasMatch === true && umsokn.value.percentOfPay !== 3) {
    umsokn.value.percentOfPay = 3
  }
  if (!umsokn.value.motframlagSelection && umsokn.value.motframlagPercent) {
    umsokn.value.motframlagSelection =
      umsokn.value.motframlagPercent === '2' || umsokn.value.motframlagPercent === '4'
        ? umsokn.value.motframlagPercent
        : 'other'
  }
  if (!umsokn.value.motframlagCustom && umsokn.value.motframlagSelection === 'other') {
    umsokn.value.motframlagCustom = umsokn.value.motframlagPercent ?? ''
  }
}

watch(() => umsokn.value?.percentOfPay, (val) => {
  if (val !== 3) {
    umsokn.value.motframlagPercent = ''
    umsokn.value.motframlagSelection = ''
    umsokn.value.motframlagCustom = ''
    umsokn.value.motframlagHasMatch = false
  } else {
    umsokn.value.motframlagHasMatch = true
  }
})

watch(() => umsokn.value?.motframlagSelection, (val) => {
  if (val === '2' || val === '4') {
    umsokn.value.motframlagPercent = val
    umsokn.value.motframlagCustom = ''
  } else if (val === 'other') {
    umsokn.value.motframlagPercent = umsokn.value.motframlagCustom
  }
})

watch(() => umsokn.value?.motframlagCustom, (val) => {
  if (umsokn.value?.motframlagSelection === 'other') {
    umsokn.value.motframlagPercent = val
  }
})

interface InvestmentPath {
  key: string
  letter: string
  name: string
  description?: string
  hlutabref?: string
  skuldabref?: string
  innlan?: string
}

const investmentPaths = computed<InvestmentPath[]>(() => [
  {
    key: 'aldurstengd',
    letter: 'A',
    name: t('sereignar_umsokn_step2_fjarfestingarleid_aldurstengd'),
    description: t('sereignar_umsokn_step2_aldurstengd_lysing'),
  },
  {
    key: 'eign7030',
    letter: 'B',
    name: t('sereignar_umsokn_step2_fjarfestingarleid_eign7030'),
    hlutabref: t('sereignar_umsokn_step2_eign7030_hlutabref'),
    skuldabref: t('sereignar_umsokn_step2_eign7030_skuldabref'),
  },
  {
    key: 'eign5050',
    letter: 'C',
    name: t('sereignar_umsokn_step2_fjarfestingarleid_eign5050'),
    hlutabref: t('sereignar_umsokn_step2_eign5050_hlutabref'),
    skuldabref: t('sereignar_umsokn_step2_eign5050_skuldabref'),
  },
  {
    key: 'eign2080',
    letter: 'D',
    name: t('sereignar_umsokn_step2_fjarfestingarleid_eign2080'),
    hlutabref: t('sereignar_umsokn_step2_eign2080_hlutabref'),
    skuldabref: t('sereignar_umsokn_step2_eign2080_skuldabref'),
  },
  {
    key: 'verdtryggd',
    letter: 'E',
    name: t('sereignar_umsokn_step2_fjarfestingarleid_verdtryggd'),
    innlan: t('sereignar_umsokn_step2_verdtryggd_innlan'),
  },
  {
    key: 'erlend',
    letter: 'F',
    name: t('sereignar_umsokn_step2_fjarfestingarleid_erlend'),
    hlutabref: t('sereignar_umsokn_step2_erlend_hlutabref'),
    skuldabref: t('sereignar_umsokn_step2_erlend_skuldabref'),
  },
])

const selectedPath = computed(() =>
  investmentPaths.value.find(p => p.key === umsokn.value.framtidarsyn)
)

function selectPath(path: InvestmentPath) {
  if (props.disable) return
  umsokn.value.framtidarsyn = path.key
}

function isSelected(path: InvestmentPath) {
  return umsokn.value.framtidarsyn === path.key
}

// Valid path keys
const validPathKeys = ['aldurstengd', 'eign7030', 'eign5050', 'eign2080', 'verdtryggd', 'erlend']

// Default to Eign-Aldurstengd (first path) when no valid selection exists
watch(
  () => umsokn.value?.id,
  (id) => {
    if (id && !props.disable && umsokn.value && !validPathKeys.includes(umsokn.value.framtidarsyn)) {
      umsokn.value.framtidarsyn = investmentPaths.value[0]?.key
    }
  },
  { immediate: true }
)

const rules = {
  umsokn: {
    percentOfPay: { required },
    framtidarsyn: { required },
    lykilupplysingarAccepted: { sameAs: sameAs(true) },
    motframlagSelection: { requiredIfYes: requiredIf(() => umsokn.value?.percentOfPay === 3) },
    motframlagPercent: { requiredIfOther: requiredIf(() => umsokn.value?.percentOfPay === 3 && umsokn.value?.motframlagSelection === 'other') },
  },
}

const v$ = useVuelidate(rules, { umsokn })

defineExpose({
  validate() {
    v$.value.$touch()
    return !v$.value.$invalid
  }
})
</script>

<template>
  <div v-if="umsokn">
    <v-row>
      <v-col>
        <h2 class="form__header">
          {{ t('sereignar_umsokn_step2_header1_new') }}
        </h2>
      </v-col>
    </v-row>

    <!-- Percent of pay -->
    <v-row>
      <v-col cols="12" md="6">
        <h3>{{ t('sereignar_umsokn_step2_oskaeftir_header') }}</h3>
        <v-radio-group
          v-model="umsokn.percentOfPay"
          :readonly="props.disable"
          hide-details="auto"
          :error="v$.umsokn.percentOfPay.$error"
          :error-messages="v$.umsokn.percentOfPay.$error ? [t('sereignar_umsokn_step2_heildarlaun_error')] : []"
        >
          <v-radio :label="t('sereignar_umsokn_step2_heildarlaun1')" :value="1" />
          <v-radio :label="t('sereignar_umsokn_step2_heildarlaun2')" :value="2" />
          <v-radio :label="t('sereignar_umsokn_step2_motframlag_header')" :value="3" />
        </v-radio-group>
        <div v-if="umsokn.percentOfPay === 3" class="motframlag-percent">
          <h3 class="motframlag-percent__title">{{ t('sereignar_umsokn_step2_motframlag_label') }}</h3>
          <v-radio-group
            v-model="umsokn.motframlagSelection"
            :readonly="props.disable"
            hide-details="auto"
            inline
            :error="v$.umsokn.motframlagSelection.$error"
            :error-messages="v$.umsokn.motframlagSelection.$error ? [t('sereignar_umsokn_step2_motframlag_percent_required')] : []"
          >
            <v-radio label="2%" value="2" />
            <v-radio label="4%" value="4" />
            <v-radio value="other">
              <template #label>
                <span>{{ t('sereignar_umsokn_step2_motframlag_other') }}</span>
                <v-text-field
                  v-if="umsokn.motframlagSelection === 'other'"
                  v-model="umsokn.motframlagCustom"
                  :readonly="props.disable"
                  variant="outlined"
                  density="compact"
                  suffix="%"
                  inputmode="numeric"
                  class="motframlag-percent__input"
                  :error="v$.umsokn.motframlagPercent.$error"
                  hide-details="auto"
                  @click.stop
                />
              </template>
            </v-radio>
          </v-radio-group>
        </div>
        <p>{{ t('sereignar_umsokn_step2_oskaeftir_texti') }}</p>

        <!-- Mótframlag % selection (shown when mótframlag is selected) -->
      </v-col>
    </v-row>

    <!-- Investment path selection -->
    <v-row>
      <v-col>
        <h3 class="path-section__title">{{ t('sereignar_umsokn_step2_veldu_fjarfestingarleid') }}</h3>
        <p class="path-section__desc">
          {{ t('umsokn_tilgreind_sereign_step3_fjarfestingarleid_intro') }}
        </p>
        <p class="path-section__desc path-section__desc--link">
          {{ t('umsokn_tilgreind_sereign_step3_lykilupplysingar_texti') }}
          <a class="link" target="_blank" :href="t('sereignar_umsokn_lykilupplysingar_url')">{{ t('umsokn_tilgreind_sereign_step3_lykilupplysingar_link') }}</a>
        </p>

        <!-- Validation error -->
        <p v-if="v$.umsokn.framtidarsyn.$error" class="path-section__error">
          {{ t('sereignar_umsokn_step2_framtidarsyn_error') }}
        </p>

        <!-- Summary mode: show only the selected path -->
        <template v-if="props.disable && selectedPath">
          <div class="path-cards">
            <div class="path-card path-card--selected">
              <div class="path-card__badge">
                <span class="path-card__letter path-card__letter--selected"></span>
              </div>
              <div class="path-card__name-col">
                <span class="path-card__name">{{ selectedPath.name }}</span>
              </div>
              <div class="path-card__middle">
                <span v-if="selectedPath.description" class="path-card__description">{{ selectedPath.description }}</span>
                <div v-else-if="selectedPath.hlutabref || selectedPath.innlan" class="path-card__allocations">
                  <span v-if="selectedPath.hlutabref" class="path-card__alloc">
                    <span class="path-card__alloc-label">{{ t('sereignar_umsokn_step2_hlutabref') }}</span>
                    <span class="path-card__alloc-value">{{ selectedPath.hlutabref }}</span>
                  </span>
                  <span v-if="selectedPath.skuldabref" class="path-card__alloc">
                    <span class="path-card__alloc-label">{{ t('sereignar_umsokn_step2_skuldabref') }}</span>
                    <span class="path-card__alloc-value">{{ selectedPath.skuldabref }}</span>
                  </span>
                  <span v-if="selectedPath.innlan" class="path-card__alloc">
                    <span class="path-card__alloc-label">{{ t('sereignar_umsokn_step2_innlan') }}</span>
                    <span class="path-card__alloc-value">{{ selectedPath.innlan }}</span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- Edit mode: full selection UI -->
        <template v-else>
          <div class="path-cards">
            <div
              v-for="path in investmentPaths"
              :key="path.key"
              class="path-card"
              :class="{ 'path-card--selected': isSelected(path) }"
            >
              <div class="path-card__badge">
                <span class="path-card__letter" :class="{ 'path-card__letter--selected': isSelected(path) }"></span>
              </div>
              <div class="path-card__name-col">
                <span class="path-card__name">{{ path.name }}</span>
              </div>
              <div class="path-card__middle">
                <span v-if="path.description" class="path-card__description">{{ path.description }}</span>
                <div v-else-if="path.hlutabref || path.innlan" class="path-card__allocations">
                  <span v-if="path.hlutabref" class="path-card__alloc">
                    <span class="path-card__alloc-label">{{ t('sereignar_umsokn_step2_hlutabref') }}</span>
                    <span class="path-card__alloc-value">{{ path.hlutabref }}</span>
                  </span>
                  <span v-if="path.skuldabref" class="path-card__alloc">
                    <span class="path-card__alloc-label">{{ t('sereignar_umsokn_step2_skuldabref') }}</span>
                    <span class="path-card__alloc-value">{{ path.skuldabref }}</span>
                  </span>
                  <span v-if="path.innlan" class="path-card__alloc">
                    <span class="path-card__alloc-label">{{ t('sereignar_umsokn_step2_innlan') }}</span>
                    <span class="path-card__alloc-value">{{ path.innlan }}</span>
                  </span>
                </div>
              </div>
              <div class="path-card__action">
                <button
                  class="path-card__btn"
                  :class="{ 'path-card__btn--active': isSelected(path) }"
                  :disabled="props.disable"
                  @click="selectPath(path)"
                >
                  {{ isSelected(path) ? t('sereignar_umsokn_step2_valin_leid') : t('sereignar_umsokn_step2_velja_leid') }}
                </button>
              </div>
            </div>
          </div>
        </template>
      </v-col>
    </v-row>

    <v-row class="pt-6">
      <v-col>
        <v-checkbox
          v-model="umsokn.lykilupplysingarAccepted"
          :readonly="props.disable"
          :label="t('sereignar_umsokn_lykilupplysingar')"
          :error="v$.umsokn.lykilupplysingarAccepted.$error"
          :error-messages="v$.umsokn.lykilupplysingarAccepted.$error ? [t('sereignar_umsokn_lykilupplysingar_error')] : []"
        />
        <div class="disclaimer">
          <a
            class="link"
            target="_blank"
            :href="t('sereignar_umsokn_disclamer_url')"
          >
            {{ t('sereignar_umsokn_disclamer') }}
          </a>
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<style lang="scss" scoped>
@use "@/scss/settings/variables" as *;

h3 {
  font-size: 22px;
  margin-bottom: 8px;
}
p {
  font-size: 16px;
  margin-top: 8px;
}
.disclaimer {
  margin-top: 20px;
  font-size: 18px;
  line-height: 28px;
}
.link {
  color: $color-primary;
  text-decoration: none;
  &:hover { text-decoration: underline; }
}

/* ── Mótframlag percent ── */
.motframlag-percent {
  margin-top: 16px;
  padding-left: 4px;
}

.motframlag-percent__title {
  font-size: 18px !important;
  margin-bottom: 4px !important;
}

.motframlag-percent__input {
  display: inline-flex;
  max-width: 120px;
  margin-left: 12px;
}

/* ── Path section ── */
.path-section__title {
  font-size: 22px;
  font-weight: 700;
  margin-bottom: 8px;
  color: $color-text;
}

.path-section__desc {
  font-size: 14px;
  line-height: 1.6;
  color: $color-text-muted;
  margin-bottom: 4px;

  &--link { margin-bottom: 20px; }
}

.path-section__error {
  color: #d32f2f;
  font-size: 14px;
  margin-bottom: 12px;
}

/* ── Card list ── */
.path-cards {
  display: flex;
  flex-direction: column;
}

.path-card {
  display: grid;
  grid-template-columns: 48px 180px 1fr auto;
  align-items: center;
  gap: 12px;
  padding: 20px 0;
  border-bottom: 1px solid $color-border;
  transition: background-color 0.15s ease;

  &:first-child { border-top: 1px solid $color-border; }
  &:hover { background-color: rgba($color-primary, 0.05); }
  &--selected {
    background-color: rgba($color-primary, 0.10);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.10), 0 0 0 1px rgba($color-primary, 0.25);
    border-radius: 8px;
    padding: 20px 12px;
  }
}

.path-card__badge {
  display: flex;
  justify-content: center;
}

.path-card__letter {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  min-width: 40px;
  border-radius: 50%;
  background-color: $color-secondary;
  color: #fff;
  font-weight: 700;
  font-size: 16px;
  transition: background-color 0.15s ease;

  &--selected {
    background-color: $color-primary;
  }
}

.path-card__name-col {
  display: flex;
  align-items: center;
}

.path-card__name {
  font-weight: 700;
  font-size: 16px;
  color: $color-secondary;
}

.path-card__middle {
  display: flex;
  align-items: center;
  min-height: 48px;
}

.path-card__description {
  font-size: 14px;
  color: $color-text-muted;
  line-height: 1.5;
  font-style: italic;
}

.path-card__allocations {
  display: flex;
  gap: 32px;
}

.path-card__alloc {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  min-width: 72px;
}

.path-card__alloc-label {
  font-size: 11px;
  color: $color-text-muted;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  line-height: 1.3;
}

.path-card__alloc-value {
  font-size: 17px;
  font-weight: 700;
  color: $color-primary;
}

.path-card__action {
  display: flex;
  justify-content: flex-end;
}

.path-card__btn {
  white-space: nowrap;
  padding: 10px 28px;
  font-size: 15px;
  font-weight: 600;
  border: 1.5px solid $color-primary;
  border-radius: 6px;
  background: transparent;
  color: $color-primary;
  cursor: pointer;
  transition: all 0.15s ease;
  min-width: 120px;
  text-align: center;

  &:hover:not(:disabled) {
    background-color: $color-primary;
    color: #fff;
  }

  &--active {
    background-color: $color-primary;
    color: #fff;
    pointer-events: none;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

/* ── Dark mode ── */
:global(.dark-mode) {
  .path-section__title { color: $color-text-dark; }
  .path-section__desc { color: $color-text-muted-dark; }
  .path-card {
    border-color: $color-border-dark;
    &:hover { background-color: rgba($color-primary-dark, 0.08); }
    &--selected {
      background-color: rgba($color-primary-dark, 0.14);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba($color-primary-dark, 0.3);
      border-radius: 8px;
      padding: 20px 12px;
    }
  }
  .path-card__letter { background-color: $color-primary-dark; color: $color-background-dark; }
  .path-card__letter--selected { background-color: $color-primary-dark; }
  .path-card__name { color: $color-text-dark; }
  .path-card__description { color: $color-text-muted-dark; }
  .path-card__alloc-label { color: $color-text-muted-dark; }
  .path-card__alloc-value { color: $color-text-dark; }
  .path-card__btn {
    border-color: $color-primary-dark;
    color: $color-primary-dark;
    &:hover:not(:disabled) { background-color: $color-primary-dark; color: $color-background-dark; }
    &--active { background-color: $color-primary-dark; color: $color-background-dark; }
  }
  .link { color: $color-primary-dark; }
}

/* ── Responsive ── */
@media (max-width: 768px) {
  .path-section__title {
    text-align: center;
  }

  .path-card {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
    gap: 8px 12px;
    padding: 16px 0;
  }

  .path-card__badge {
    order: 1;
    flex-shrink: 0;
    width: 100%;
    display: flex;
    justify-content: center;
  }

  .path-card__name-col {
    order: 2;
    width: 100%;
    text-align: center;
  }

  .path-card__name {
    display: block;
    width: 100%;
    text-align: center;
  }

  .path-card__middle {
    order: 3;
    width: 100%;
    min-height: auto;
    display: flex;
    justify-content: center;
  }

  .path-card__description {
    text-align: center;
  }

  .path-card__allocations {
    justify-content: center;
    gap: 20px;
  }

  .path-card__action {
    order: 4;
    width: 100%;
  }

  .path-card__btn {
    width: 100%;
  }
}
</style>
